import { createApiCall } from "services/api/createApiCall";
import { transformErrorResponse } from "services/api/transformation";

import {
  Subscription,
  SubscriptionBilling,
  SubscriptionEstimate,
  SubscriptionSignFormValues,
  SubscriptionStepFormValues,
} from "./types";

const transformSubscription = (res: any): Subscription => ({
  id: res.id,
  tenantId: res.tenantId,
  packCombinationKey: res.packCombinationKey,
  packCodes: res.packCodes ?? [],
  billingPeriod: res.billingPeriod,
  status: res.status,
  startsOn: res.startsOn,
  trialEndsOn: res.trialEndsOn ?? null,
  cancelledOn: res.cancelledOn ?? null,
  created: res.created,
});

/** Backend očekuje broj: 0 = Monthly, 1 = Annual. */
const toBillingPeriodNumber = (period: string): number => (period === "Annual" ? 1 : 0);

/** Backend očekuje broj: 0 = Percent, 1 = Absolute. */
const toAdjustmentNumber = (type: string): number => (type === "Absolute" ? 1 : 0);

/*---------------------------------------------------------------------------------------
    PRETPLATA KLIJENTA

    Prazno je uredno stanje, ne greška — klijent bez pretplate je 404, i to se ne javlja
    korisniku porukom.
-----------------------------------------------------------------------------------------*/
export const getTenantSubscriptionAPI = async (
  tenantId: string
): Promise<Subscription | null> => {
  try {
    const response = await createApiCall(
      { path: `/v1/admin/tenants/${tenantId}/subscription`, method: "GET" },
      {
        getMessage: {
          error: (error: any) =>
            error?.response?.status === 404 ? "" : `${transformErrorResponse(error)}`,
        },
      }
    )();

    return transformSubscription(response.data);
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }

    throw error;
  }
};

const transformEstimate = (res: any): SubscriptionEstimate => ({
  combinationKey: res.combinationKey ?? null,
  unitPrice: res.unitPrice,
  minimum: res.minimum,
  priceTiers: (res.priceTiers ?? []).map((t: any) => ({
    fromVehicle: t.fromVehicle,
    toVehicle: t.toVehicle ?? null,
    ratePercent: t.ratePercent,
    vehiclesInTier: t.vehiclesInTier,
    amount: t.amount,
  })),
  annualDiscountPercent: res.annualDiscountPercent,
  trialDays: res.trialDays,
  trialEndsOn: res.trialEndsOn ?? null,
  metered: (res.metered ?? []).map((m: any) => ({
    keyCode: m.keyCode,
    quantity: m.quantity,
    unitPrice: m.unitPrice,
    amount: m.amount,
  })),
  amounts: res.estimate
    ? {
        byVehicles: res.estimate.byVehicles,
        minimum: res.estimate.minimum,
        fullMonth: res.estimate.fullMonth,
        isMinimumApplied: res.estimate.isMinimumApplied,
        firstChargedOn: res.estimate.firstChargedOn,
        chargedDays: res.estimate.chargedDays,
        daysInMonth: res.estimate.daysInMonth,
        firstMonth: res.estimate.firstMonth,
        meteredTotal: res.estimate.meteredTotal,
        monthlyTotal: res.estimate.monthlyTotal,
        annualTotal: res.estimate.annualTotal ?? null,
      }
    : null,
  errors: res.errors ?? [],
});

/*---------------------------------------------------------------------------------------
    OBRAČUN

    Isti izračun kao procjena, ali po stvarnom broju vozila i po paketima iz potpisane
    pretplate. Ništa se ne sprema — ovo je prikaz, ne naplata.
-----------------------------------------------------------------------------------------*/
export const getTenantSubscriptionBillingAPI = async (
  tenantId: string
): Promise<SubscriptionBilling> => {
  const response = await createApiCall(
    { path: `/v1/admin/tenants/${tenantId}/subscription/billing`, method: "GET" },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  const data = response.data;

  return {
    subscription: data.subscription ? transformSubscription(data.subscription) : null,
    vehicleCount: data.vehicleCount ?? 0,
    // Datum razlaganja je isti za sve ključeve, pa stoji na vrhu odgovora, ne po stavci.
    unitPrice: data.unitPrice
      ? {
          keyCode: data.unitPrice.keyCode,
          onDate: data.onDate,
          standard: data.unitPrice.standard,
          afterScheme: data.unitPrice.afterScheme,
          effective: data.unitPrice.effective,
          discountPercent: data.unitPrice.discountPercent,
        }
      : null,
    minimum: data.minimum
      ? {
          keyCode: data.minimum.keyCode,
          onDate: data.onDate,
          standard: data.minimum.standard,
          afterScheme: data.minimum.afterScheme,
          effective: data.minimum.effective,
          discountPercent: data.minimum.discountPercent,
        }
      : null,
    billing: data.billing ? transformEstimate(data.billing) : null,
    errors: data.errors ?? [],
  };
};

/*---------------------------------------------------------------------------------------
    PROCJENA

    Ništa se ne sprema. Ista pravila i isti resolver kao pri potpisu, pa se procjena i
    potpisani iznos ne mogu razići.
-----------------------------------------------------------------------------------------*/
export const estimateTenantSubscriptionAPI = async (
  tenantId: string,
  formValues: SubscriptionStepFormValues
): Promise<SubscriptionEstimate> => {
  const response = await createApiCall(
    {
      path: `/v1/admin/tenants/${tenantId}/subscription/estimate`,
      method: "POST",
      body: {
        packCodes: formValues.packCodes,
        billingPeriod: toBillingPeriodNumber(formValues.billingPeriod),
        startsOn: formValues.startsOn,
        pricingSchemeId: formValues.pricingSchemeId,
        withTrial: formValues.withTrial,
        vehicleCount: formValues.estimatedVehicles ?? 0,
        meteredQuantities: formValues.estimatedQuantities,
      },
    },
    { getMessage: { error: (error: any) => `${transformErrorResponse(error)}` } }
  )();

  return transformEstimate(response.data);
};

/*---------------------------------------------------------------------------------------
    IZMJENA

    Pretplata zadržava id i datum početka; mijenjaju se paketi, način plaćanja i grupa.
    Promjena paketa se bilježi kroz razdoblja, pa dnevni obračun zna što je vrijedilo kad.
-----------------------------------------------------------------------------------------*/
export const updateTenantSubscriptionAPI = async (
  tenantId: string,
  values: {
    packCodes: string[];
    billingPeriod: string;
    pricingSchemeId: string | null;
    effectiveFrom: string | null;
  }
): Promise<Subscription> => {
  const response = await createApiCall(
    {
      path: `/v1/admin/tenants/${tenantId}/subscription`,
      method: "PATCH",
      body: {
        packCodes: values.packCodes,
        billingPeriod: toBillingPeriodNumber(values.billingPeriod),
        pricingSchemeId: values.pricingSchemeId,
        effectiveFrom: values.effectiveFrom,
      },
    },
    {
      getMessage: {
        success: () => "Pretplata je izmijenjena",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformSubscription(response.data);
};

/*---------------------------------------------------------------------------------------
    POTPIS

    Jedan poziv: packovi, shema, način plaćanja i pregazivanja zajedno. Backend ih sprema u
    istoj transakciji i tek onda snima snapshot cijena.
-----------------------------------------------------------------------------------------*/
export const signTenantSubscriptionAPI = async (
  tenantId: string,
  formValues: SubscriptionSignFormValues
): Promise<Subscription> => {
  const response = await createApiCall(
    {
      path: `/v1/admin/tenants/${tenantId}/subscription`,
      method: "POST",
      body: {
        packCodes: formValues.packCodes,
        billingPeriod: toBillingPeriodNumber(formValues.billingPeriod),
        startsOn: formValues.startsOn,
        pricingSchemeId: formValues.pricingSchemeId,
        withTrial: formValues.withTrial,
        overrides: formValues.overrides.map((item) => ({
          keyCode: item.keyCode,
          adjustmentType: toAdjustmentNumber(item.adjustmentType),
          value: item.value,
          reason: item.reason,
          validFrom: item.validFrom,
          validTo: item.validTo,
        })),
      },
    },
    {
      getMessage: {
        success: () => "Pretplata je spremljena",
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  return transformSubscription(response.data);
};
